import type { Vec } from './types.ts';

export const TAU = Math.PI * 2;
export function clamp(v: number, a: number, b: number): number { return v < a ? a : v > b ? b : v; }
export function angleDiff(a: number, b: number): number {
  let d = b - a;
  while (d > Math.PI) d -= TAU;
  while (d < -Math.PI) d += TAU;
  return d;
}
export function normAngle(a: number): number { return Math.atan2(Math.sin(a), Math.cos(a)); }
export function dist(a: Vec, b: Vec): number { return Math.hypot(a.x - b.x, a.y - b.y); }
export function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
export function lerpAngle(a: number, b: number, t: number): number { return a + angleDiff(a, b) * t; }

/** total polyline length */
export function pathLength(pts: Vec[]): number {
  let L = 0;
  for (let i = 1; i < pts.length; i++) L += dist(pts[i - 1], pts[i]);
  return L;
}

/** Resample a polyline so consecutive points are ~`step` apart (keeps endpoints). */
export function resample(pts: Vec[], step: number): Vec[] {
  if (pts.length < 2) return pts.slice();
  const out: Vec[] = [{ ...pts[0] }];
  let carry = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const seg = dist(a, b);
    if (seg === 0) continue;
    let d = step - carry;
    while (d <= seg) {
      const t = d / seg;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
      d += step;
    }
    carry = seg - (d - step);
  }
  const last = pts[pts.length - 1];
  const tail = out[out.length - 1];
  if (dist(tail, last) > step * 0.35) out.push({ ...last });
  else { tail.x = last.x; tail.y = last.y; }
  return out;
}

/** One pass of a 3-tap moving average, endpoints fixed. Gentle smoothing for hand-drawn ink. */
export function smooth(pts: Vec[], passes = 1): Vec[] {
  let cur = pts;
  for (let p = 0; p < passes; p++) {
    if (cur.length < 3) return cur.slice();
    const out: Vec[] = [{ ...cur[0] }];
    for (let i = 1; i < cur.length - 1; i++) {
      out.push({
        x: (cur[i - 1].x + 2 * cur[i].x + cur[i + 1].x) / 4,
        y: (cur[i - 1].y + 2 * cur[i].y + cur[i + 1].y) / 4,
      });
    }
    out.push({ ...cur[cur.length - 1] });
    cur = out;
  }
  return cur;
}

/** Point on an arc: centre c, radius r, angle a */
export function onCircle(c: Vec, r: number, a: number): Vec { return { x: c.x + Math.cos(a) * r, y: c.y + Math.sin(a) * r }; }

/**
 * The nearest runway threshold or helipad centre to a point, and how far it is. The sound
 * layer's tonic centre (docs/research/music.md): every ring around it is a scale degree, so an
 * approach descends the scale and a landing is a cadence. Pads count as thresholds too, so a
 * heli approach resolves the same way. Falls back to the layout centre on a layout with neither.
 */
export function nearestThreshold(
  layout: { runways: readonly { x1: number; y1: number }[]; pads: readonly { x: number; y: number }[]; cx: number; cy: number },
  x: number, y: number,
): { x: number; y: number; d: number } {
  let best = { x: layout.cx, y: layout.cy, d: Infinity };
  for (const r of layout.runways) {
    const d = Math.hypot(x - r.x1, y - r.y1);
    if (d < best.d) best = { x: r.x1, y: r.y1, d };
  }
  for (const p of layout.pads) {
    const d = Math.hypot(x - p.x, y - p.y);
    if (d < best.d) best = { x: p.x, y: p.y, d };
  }
  if (best.d === Infinity) best.d = Math.hypot(x - layout.cx, y - layout.cy);
  return best;
}
